import React, { FC } from 'react';
import { IntlProvider } from 'react-intl';
import { Store } from 'redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import isDomUsable from '@fenderdigital/dom';
import DeveloperConsole from './DeveloperConsole/DeveloperConsoleContainer';
import Layout from './Layout/LayoutContainer';
import Styles from './Styles';
import routes from '../routes';
import NotFound from '../routes/404/NotFound';
import { useTrackPageChange } from '../hooks/useTrackPageChange';

export interface AppProps {
  queryParams: URLSearchParams;
  store: Store;
}

const App: FC<AppProps> = ({ queryParams, store }) => {
  useTrackPageChange();
  const state = store && store.getState();
  const intl = (state && state.intl) || {};
  const { locale, messages } = intl;
  const localeMessages = (messages && messages[locale]) || {};
  return (
    <IntlProvider
      initialNow={Date.now()}
      locale="en-US"
      messages={localeMessages}
      defaultLocale="en-US"
    >
      <DeveloperConsole>
        <Styles>
          <Layout>
            <Switch>
              <Route
                exact
                path={['/lesson', '/lessons', '/course', '/courses', '/practice']}
                render={() => <Redirect to="/" />}
              />
              <Route exact path="/artists" render={() => <Redirect to="/guitar/artists" />} />
              <Route
                exact
                path={['/skills', '/skills/featured']}
                render={() => <Redirect to="/guitar/skills" />}
              />
              <Route
                exact
                path="/:instrument(bass|guitar|ukulele)"
                render={({ match }) => <Redirect to={`/${match.params.instrument}/songs`} />}
              />
              <Route
                exact
                path="/:instrument(bass|guitar|ukulele)/artists"
                render={({ match }) => (
                  <Redirect to={`/${match.params.instrument}/artists/songs`} />
                )}
              />
              <Route exact path="/my-path" render={() => <Redirect to="/path" />} />
              <Route exact path="/songs/catalog" render={() => <Redirect to="/songs" />} />
              <Route exact path="/onboarding" render={() => <Redirect to="/plans" />} />
              <Route
                exact
                path={['/onboarding/signin-reseller', '/onboarding/create-password']}
                render={() => {
                  if (queryParams.has('email')) {
                    const redirSearchParams = new URLSearchParams();
                    redirSearchParams.append('email', queryParams.get('email') || '');
                    redirSearchParams.append('token', queryParams.get('token') || '');
                    return (
                      <Redirect
                        to={{ pathname: '/signin', search: redirSearchParams.toString() }}
                      />
                    );
                  }
                  return <Redirect to="/plans" />;
                }}
              />
              <Route
                exact
                path="/onboarding/renew"
                render={() => <Redirect to={{ pathname: '/plans', search: '?signin=true' }} />}
              />
              <Route exact path="/onboarding/:subroute" render={() => <Redirect to="/plans" />} />
              {routes.map(({ path, Component, exact }) => (
                <Route key={path} path={path} component={Component} exact={exact} />
              ))}

              <Route
                exact
                path="/signin"
                render={() => {
                  if (isDomUsable()) {
                    window.location.replace(`${process.env.REACT_APP_CLIENT_SSO_URL}/sign-in`);
                  }
                  return null;
                }}
              />
              <Route
                exact
                path="/signup"
                render={() => {
                  if (isDomUsable()) {
                    window.location.replace(`${process.env.REACT_APP_CLIENT_SSO_URL}/sign-up`);
                  }
                  return null;
                }}
              />
              <Route
                exact
                path="/:instrument(bass|guitar|ukulele)/:type"
                render={({ match }) => <Redirect to={`/${match.params.instrument}/songs`} />}
              />
              <Route component={NotFound} />
            </Switch>
          </Layout>
        </Styles>
      </DeveloperConsole>
    </IntlProvider>
  );
};

export default App;
